import { useContext, useEffect } from 'react';

import { ReactReduxContext } from 'react-redux';
import { nativePrintSession, useNativePrintMode } from '~/nativeLenses/nativePrintSession';

import type { MigrationReportRow } from '~/types/migrationWelcome';
import type { RootState } from '~/types/store';

import { trackMigrationEvent } from './migrationTelemetry';

export const MIGRATION_OPEN_STORAGE_KEY = 'askrichard:migration:pending-open:v1';
export const MIGRATION_OPEN_MAX_AGE_MS = 10 * 60 * 1000;

export type PendingMigrationOpen = {
  viewLuid: string;
  destinationType: 'lens' | 'native';
  destinationId: string;
  email: string;
  startedAt: number;
};

type MigrationDestination = {
  destinationType: 'lens' | 'native';
  destinationId: string;
  email: string;
};

export function matchesPendingMigrationOpen(
  pending: PendingMigrationOpen,
  destination: MigrationDestination,
  now = Date.now(),
): boolean {
  const age = now - pending.startedAt;
  return (
    Number.isFinite(age) &&
    age >= 0 &&
    age <= MIGRATION_OPEN_MAX_AGE_MS &&
    pending.email === destination.email.trim().toLowerCase() &&
    pending.destinationType === destination.destinationType &&
    pending.destinationId === destination.destinationId
  );
}

export function beginMigrationReportOpen(row: MigrationReportRow, email?: string | null): void {
  if (!email || !row.canOpen || !row.destinationId || row.destinationType === 'none') {
    return;
  }
  try {
    const pending: PendingMigrationOpen = {
      viewLuid: row.viewLuid,
      destinationType: row.destinationType,
      destinationId: row.destinationId,
      email: email.trim().toLowerCase(),
      startedAt: Date.now(),
    };
    sessionStorage.setItem(MIGRATION_OPEN_STORAGE_KEY, JSON.stringify(pending));
  } catch {
    // Navigation still works when session storage is unavailable.
  }
}

/** Called only by a destination's successful real-data boundary, never by route changes. */
export function completeMigrationReportOpen(destination: MigrationDestination): boolean {
  try {
    const stored = sessionStorage.getItem(MIGRATION_OPEN_STORAGE_KEY);
    if (!stored) {
      return false;
    }
    const pending = JSON.parse(stored) as PendingMigrationOpen;
    if (
      typeof pending.viewLuid !== 'string' ||
      typeof pending.email !== 'string' ||
      typeof pending.destinationId !== 'string' ||
      typeof pending.startedAt !== 'number' ||
      !Number.isFinite(pending.startedAt)
    ) {
      sessionStorage.removeItem(MIGRATION_OPEN_STORAGE_KEY);
      return false;
    }
    if (
      pending.email !== destination.email.trim().toLowerCase() ||
      Date.now() - pending.startedAt > MIGRATION_OPEN_MAX_AGE_MS
    ) {
      sessionStorage.removeItem(MIGRATION_OPEN_STORAGE_KEY);
      return false;
    }
    if (!matchesPendingMigrationOpen(pending, destination)) {
      return false;
    }
    // Consume before tracking so concurrent data hooks and React StrictMode cannot double count.
    sessionStorage.removeItem(MIGRATION_OPEN_STORAGE_KEY);
    trackMigrationEvent('row_open_ok', pending.email, pending.viewLuid, pending.destinationId);
    return true;
  } catch {
    return false;
  }
}

/** Optional Redux context keeps isolated report/bridge consumers independent of an app provider. */
export function useMigrationReportSuccess(
  destinationType: 'lens' | 'native',
  destinationId: string | undefined,
  ready: boolean,
): void {
  const redux = useContext(ReactReduxContext);
  const printMode = useNativePrintMode();
  const email = (redux?.store.getState() as RootState | undefined)?.currentUser?.user?.email;
  useEffect(() => {
    if (!ready || !destinationId || !email || printMode || nativePrintSession()) {
      return;
    }
    const params = new URLSearchParams(window.location.search);
    if (params.has('sample') && params.get('sample') !== '0') {
      return;
    }
    completeMigrationReportOpen({ destinationType, destinationId, email });
  }, [destinationType, destinationId, ready, email, printMode]);
}
